import {
  Tooltip,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import QB from "../../assets/logo/qb.svg";
import clsx from "clsx";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../primitives/Button";
import { ButtonVariantType } from "../../primitives/Button/types";
import {
  NavChildItem,
  NavItem,
  OpenGroupsState,
  SidebarProps,
} from "../../types/sidebar";

import { Logout } from "../../assets/icons";
import { RestrictedComponent } from "../../components/RestrictedComponent";

export const Sidebar = ({
  mobileOpen,
  handleDrawerToggle,
  sidebarOpen,
  isMobile,
  bannerOffset: _bannerOffset = 0,
  navItems,
  removeAuthData,
  user,
  tourOpenGroups,
}: SidebarProps) => {
  const location = useLocation();
  const { t } = useTranslation("common");
  const sidebarTitle = user?.admin ? "Admin" : t("common.salesBot");
  const navigate = useNavigate();
  const drawerWidth = "16.25rem";
  const collapsedWidth = "3.5rem";

  const [openGroups, setOpenGroups] = useState<OpenGroupsState>({});
  const isPathActive = (paths: string[]) =>
    paths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(path + "/")
    );
  useEffect(() => { 
    const initialOpen: Record<string, boolean> = {}; 
    navItems.forEach((item: NavItem) => {
      if (item.children && item.children.length > 0) {
        const childActive = item.children.some((child: NavChildItem) =>
          isPathActive(child.matchPaths || [child.path])
        );
        initialOpen[item.label] = childActive;
      }
    });
    setOpenGroups((prev) => ({ ...prev, ...initialOpen }));
  }, [location.pathname]);

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Merge forced-open groups from the tour without overriding user selections
  const effectiveOpenGroups: OpenGroupsState = tourOpenGroups
    ? { ...openGroups, ...tourOpenGroups }
    : openGroups;
  const [open, setOpen] = useState(false);

  const [collapsedHoverItem, setCollapsedHoverItem] = useState<NavItem | null>(
    null
  );
  const [collapsedAnchorEl, setCollapsedAnchorEl] =
    useState<HTMLElement | null>(null);
  const [hoveringPopover, setHoveringPopover] = useState<boolean>(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleItemMouseEnter = (
    item: NavItem,
    e: React.MouseEvent<HTMLElement>
  ) => {
    if (sidebarOpen) return;
    clearCloseTimeout();
    if (item.children && item.children.length > 0) {
      setCollapsedHoverItem(item);
      setCollapsedAnchorEl(e.currentTarget);
    } else {
      setCollapsedHoverItem(null);
      setCollapsedAnchorEl(null);
    }
  };

  const handleItemMouseLeave = () => {
    if (sidebarOpen) return;
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!hoveringPopover) {
        setCollapsedHoverItem(null);
        setCollapsedAnchorEl(null);
      }
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    clearCloseTimeout();
    setHoveringPopover(true);
  };

  const handlePopoverMouseLeave = () => {
    setHoveringPopover(false);
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setCollapsedHoverItem(null);
      setCollapsedAnchorEl(null);
    }, 150);
  };

  useEffect(() => {
    if (sidebarOpen) {
      clearCloseTimeout();
      setCollapsedHoverItem(null);
      setCollapsedAnchorEl(null);
    }
  }, [sidebarOpen, location.pathname]);

  const handleLogout = () => {
    removeAuthData();
    setOpen(false);
  };
  const mobileDrawerContent = (
    <div
      className={` h-screen flex flex-col bg-sidebar shadow-light`}
    >
      <div className="flex items-center justify-between px-4 py-3 shrink-0 border-b border-white/10">
        <div className="flex items-center">
          <img
            src={QB}
            alt="Logo"
            className="w-8 h-8 object-contain rounded-full"
          />
          <h1 className="ml-2 font-bold text-lg">
            {sidebarTitle}
          </h1>
        </div>
        <IconButton
          onClick={handleDrawerToggle}
          className="hover:bg-sidebar/10"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </div>

      <div id="sidebar-scroll-container-mobile" className="flex-1 overflow-y-auto px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <ul className="pr-2">
          {navItems.map((item: NavItem) => {
            const active = isPathActive(item.matchPaths || [item.path]);
            const hasChildren =
              Array.isArray(item.children) && item.children.length > 0;
            const parentActive = active;

            return (
              <RestrictedComponent key={item.label} permission={item?.permission}>
                <li>
                  <button
                    id={item.id}
                    onClick={() => {
                      if (hasChildren) {
                        toggleGroup(item.label);
                      } else {
                        navigate(item.path);
                        handleDrawerToggle();
                      }
                    }}
                    className={clsx(
                      "relative flex items-center justify-between w-full rounded-xl my-1 px-4 py-2 text-left transition-all duration-200 min-h-11 font-bold border",
                      parentActive
                        ? "bg-gradient-primary text-sidebar-menu-active-foreground shadow-purple-sm border-transparent"
                        : "text-sidebar-menu-foreground border-transparent hover:border-sidebar-menu-border-hover hover:bg-sidebar-opacity-15"
                    )}
                  >
                    <span className="flex items-center">
                      <span
                        className={clsx(
                          "",
                          parentActive && "[&_svg_path]:stroke-white!"
                        )}
                      >
                        <item.icon />
                      </span>
                      <span className="ml-2 text-[0.875rem]">{item.label}</span>
                    </span>
                    {hasChildren &&
                      (effectiveOpenGroups[item.label] ? (
                        <ExpandMoreIcon fontSize="small" />
                      ) : (
                        <ChevronRightIcon fontSize="small" />
                      ))}
                  </button>

                  {/* Submenu */}
                  {hasChildren && effectiveOpenGroups[item.label] && (
                    <ul className="ml-8 mb-2">
                      {item.children!.map((child: NavChildItem) => {
                        const childActive = isPathActive(
                          child.matchPaths || [child.path]
                        );
                        return (
                          <RestrictedComponent key={child.label} permission={child?.permission}>
                            <li>
                              <button
                                id={child.id}
                                onClick={() => {
                                  navigate(child.path);
                                  handleDrawerToggle();
                                }}
                                className={clsx(
                                  "w-full text-left px-2 py-2 rounded-lg text-[0.875rem] mb-1 font-bold border ",
                                  childActive
                                    ? "bg-gradient-primary text-sidebar-menu-active-foreground border-transparent"
                                    : "text-sidebar-menu-foreground border-transparent hover:border-sidebar-menu-border-hover hover:bg-black/1"
                                )}
                              >
                                {child.label}
                              </button>
                            </li>
                          </RestrictedComponent>
                        );
                      })}
                    </ul>
                  )}
                </li>
              </RestrictedComponent>
            );
          })}
        </ul>
      </div>

      {/* User Card (fixed bottom) */}
      <motion.div
        className="p-2 shrink-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.1 }}
      >
        <motion.div className="rounded-xl p-2 mb-2 flex flex-col gap-4 items-start">
          <div className="flex justify-between w-full items-center">
            <div className="flex items-center gap-1">{user?.admin ? "Log Out" : t("common.logout")}</div>
            <IconButton color="error" onClick={() => setOpen(true)}>
              <Tooltip title={user?.admin ? "Log Out" : t("common.logout")} arrow>
                <Logout />
              </Tooltip>
            </IconButton>
          </div>
        </motion.div>
      </motion.div >
    </div >
  );

  const desktopDrawerContent = (
    <motion.div
      initial={{ width: collapsedWidth }}
      animate={{ width: sidebarOpen ? drawerWidth : collapsedWidth }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="flex flex-col bg-sidebar px-2 shadow-light h-full"
    >
      <div
        className={clsx(
          "flex items-center py-4  shrink-0",
          sidebarOpen ? "justify-start gap-2 px-2" : "justify-center"
        )}
      >
        <img src={QB} alt="Logo" className="object-contain" />

        <motion.div
          className="overflow-hidden"
          initial={false}
          animate={{
            width: sidebarOpen ? "auto" : 0,
            opacity: sidebarOpen ? 1 : 0,
          }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <h1 className="text-[1rem] font-semibold tracking-wide text-sidebar-logo whitespace-nowrap">
            {sidebarTitle}
          </h1>
        </motion.div>
      </div>

      <div id="sidebar-scroll-container" className="flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <ul className="space-y-3 pb-4">
          {navItems.map((item: NavItem) => {
            const active = isPathActive(item.matchPaths || [item.path]);
            const hasChildren =
              Array.isArray(item.children) && item.children.length > 0;
            const parentActive =
              active ||
              (hasChildren &&
                item.children!.some((child: NavChildItem) =>
                  isPathActive(child.matchPaths || [child.path])
                ));

            return (
              <RestrictedComponent key={item.label} permission={item?.permission}>
                <li>
                  <Tooltip
                    title={!sidebarOpen && !hasChildren ? item.label : ""}
                    placement="right"
                    arrow
                    disableHoverListener={sidebarOpen || hasChildren}
                  >
                    <motion.button
                      id={item.id}
                      onClick={() => {
                        if (hasChildren && sidebarOpen) {
                          toggleGroup(item.label);
                        } else if (!hasChildren) {
                          navigate(item.path);
                          if (isMobile) handleDrawerToggle();
                        }
                      }}
                      onMouseEnter={(e) => handleItemMouseEnter(item, e)}
                      onMouseLeave={handleItemMouseLeave}
                      className={clsx(
                        "relative w-full flex items-center rounded-lg cursor-pointer mb-1 transition-all duration-200 ease-in-out",
                        sidebarOpen
                          ? "justify-between px-3 py-2"
                          : "justify-center px-3 py-2",
                        parentActive
                          ? item.children
                            ? "border border-sidebar-menu-border-hover bg-sidebar-opacity-15"
                            : "bg-gradient-primary text-sidebar-menu-active-foreground cursor-pointer"
                          : "text-sidebar-menu-foreground border border-transparent hover:border-sidebar-menu-border-hover hover:bg-sidebar-opacity-15"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={clsx(
                            "text-xl ",
                            item.children
                              ? ""
                              : parentActive && "[&_svg_path]:stroke-white!"
                          )}
                        >
                          <item.icon />
                        </span>
                        {sidebarOpen && (
                          <motion.span className="text-[0.875rem] font-bold  overflow-hidden whitespace-nowrap">
                            {item.label}
                          </motion.span>
                        )}
                      </span>

                      {sidebarOpen && hasChildren && (
                        <span className="flex items-center gap-1">
                          {effectiveOpenGroups[item.label] ? (
                            <ExpandMoreIcon fontSize="small" />
                          ) : (
                            <ChevronRightIcon fontSize="small" />
                          )}
                        </span>
                      )}
                    </motion.button>
                  </Tooltip>

                  {/* Inline submenu when sidebar is open */}
                  <AnimatePresence>
                    {hasChildren && effectiveOpenGroups[item.label] && sidebarOpen && (
                      <motion.ul
                        className="ml-5 mb-2"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        {item.children!.map((child: NavChildItem) => (
                          <RestrictedComponent key={child.label} permission={child?.permission}>
                            <li>
                              <motion.button
                                id={child.id}
                                onClick={() => {
                                  navigate(child.path);
                                  if (isMobile) handleDrawerToggle();
                                }}
                                className={clsx(
                                  "w-full text-left px-3 py-2 rounded-lg text-[0.875rem] mb-1 cursor-pointer font-bold border",
                                  isPathActive(child.matchPaths || [child.path])
                                    ? "bg-gradient-primary text-sidebar-menu-active-foreground cursor-pointer border-transparent"
                                    : "text-sidebar-menu-foreground border-transparent hover:border-sidebar-menu-border-hover hover:bg-sidebar-opacity-15"
                                )}
                              >
                                {child.label}
                              </motion.button>
                            </li>
                          </RestrictedComponent>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              </RestrictedComponent>
            );
          })}
        </ul>
      </div>

      {/* Popover submenu when sidebar is collapsed */}
      {!sidebarOpen && (
        <Popover
          open={Boolean(
            collapsedAnchorEl &&
              collapsedHoverItem &&
              collapsedHoverItem.children?.length
          )}
          anchorEl={collapsedAnchorEl}
          onClose={() => {
            clearCloseTimeout();
            setCollapsedHoverItem(null);
            setCollapsedAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableRestoreFocus
          disableAutoFocus
          disableEnforceFocus
          slotProps={{
            backdrop: {
              sx: { pointerEvents: "none" },
            },
          }}
          sx={{
            pointerEvents: "none",
          }}
          PaperProps={{
            className: "bg-black rounded-lg border border-gray-200",
            sx: {
              pointerEvents: "auto",
              overflow: "visible",
            },
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
          }}
        >
          {collapsedHoverItem && collapsedHoverItem.children && (
            <ul className="flex flex-col gap-1 min-w-45 bg-sidebar p-1">
              {collapsedHoverItem.children.map((child: NavChildItem) => {
                const childActive = isPathActive(
                  child.matchPaths || [child.path]
                );
                return (
                  <RestrictedComponent
                    key={child.label}
                    permission={child?.permission}
                  >
                    <li>
                      <button
                        onClick={() => {
                          navigate(child.path);
                          clearCloseTimeout();
                          setCollapsedHoverItem(null);
                          setCollapsedAnchorEl(null);
                          if (isMobile) handleDrawerToggle();
                        }}
                        className={clsx(
                          "text-left px-3 py-2 w-full rounded-md text-sm transition-colors duration-200 cursor-pointer border",
                          childActive
                            ? "bg-gradient-primary text-sidebar-menu-active-foreground font-medium shadow-md border-transparent"
                            : "text-sidebar-child-menu-foreground border-transparent hover:border-sidebar-menu-border-hover hover:bg-sidebar-opacity-15"
                        )}
                      >
                        {child.label}
                      </button>
                    </li>
                  </RestrictedComponent>
                );
              })}
            </ul>
          )}
        </Popover>
      )}

      <motion.div
        className="p-2 shrink-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.1 }}
      >
        <AnimatePresence mode="wait">
          {sidebarOpen ? (
            <motion.div className="rounded-xl p-2 mb-2 text-sidebar-logout flex flex-col gap-4 items-start">
              <div className="flex justify-between w-full items-center">
                <div className="flex items-center gap-1">{user?.admin ? "Log Out" : t("common.logout")}</div>
                <IconButton color="error" onClick={() => setOpen(true)}>
                  <Tooltip title={user?.admin ? "Log Out" : t("common.logout")} arrow>
                    <Logout />
                  </Tooltip>
                </IconButton>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="collapsed-footer"
              className="p-2 mb-2 flex items-center justify-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.1 }}
            >
              <IconButton color="error" onClick={() => setOpen(true)}>
                <Tooltip title={user?.admin ? "Log Out" : t("common.logout")} arrow>
                  <Logout />
                </Tooltip>
              </IconButton>
            </motion.div >
          )}
        </AnimatePresence >
      </motion.div >
    </motion.div >
  );

  return (
    <>
      {mobileOpen && (
        <AnimatePresence>
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-50 block md:hidden bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleDrawerToggle}
          >
            <motion.div
              key="drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.2, ease: "easeInOut" }}
              className="h-full shadow-lg"
              style={{ width: drawerWidth }}
              onClick={(e) => e.stopPropagation()}
            >
              {mobileDrawerContent}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
      <div
        className="hidden md:block overflow-hidden h-full"
        style={{
          zIndex: 100,
          paddingBottom: 0,
        }}
      >
        <motion.div
          initial={false}
          animate={{ width: sidebarOpen ? drawerWidth : collapsedWidth }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          style={{ willChange: "width" }}
          className="h-full overflow-hidden"
        >
          <div className="h-full" style={{ width: drawerWidth }}>{desktopDrawerContent}</div>
        </motion.div>
      </div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        // slots={{
        //   transition: Transition,
        // }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            width: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
          {user?.admin ? "Are you sure you want to log out?" : t("common.logoutConfirmation")}
        </DialogTitle>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1.5,
            pb: 2,
          }}
        >
          <Button
            variantType={ButtonVariantType.PRIMARY_OUTLINED}
            onClick={() => setOpen(false)}
          >
            {user?.admin ? "Cancel" : t("common.cancel")}
          </Button >
          <Button variantType={ButtonVariantType.DANGER} onClick={handleLogout}>
            {user?.admin ? "Log Out" : t("common.logout")}
          </Button>
        </DialogActions >
      </Dialog >
    </>
  );
};
